import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';
import { useToast } from '@/hooks/use-toast';

const log = getLogger({ module: 'RealtimeNotifications' });

interface ContentUpdate {
  type: 'new_content' | 'trending' | 'constellation_update';
  content_id: string;
  title?: string;
  description?: string;
  user_name?: string;
  timestamp: string;
}

interface NotificationPreferences {
  newContent: boolean;
  trending: boolean;
  constellationUpdates: boolean;
  soundEnabled: boolean;
  minimumScore?: number;
}

export const useRealTimeNotifications = (
  preferences: NotificationPreferences = {
    newContent: true,
    trending: true,
    constellationUpdates: true,
    soundEnabled: false
  }
) => {
  const { toast } = useToast();
  const channelRef = useRef<any>(null);
  const notificationQueueRef = useRef<ContentUpdate[]>([]);
  const processingRef = useRef(false);
  const hasShownConnectedToast = useRef(false);
  
  // Batch process notifications to avoid spam
  const processNotificationQueue = useCallback(async () => {
    if (processingRef.current || notificationQueueRef.current.length === 0) {
      return;
    }
    
    processingRef.current = true;
    const queue = [...notificationQueueRef.current];
    notificationQueueRef.current = [];
    
    try {
      // Group similar notifications
      const grouped = queue.reduce((acc, notification) => {
        const key = notification.type;
        if (!acc[key]) acc[key] = [];
        acc[key].push(notification);
        return acc;
      }, {} as Record<string, ContentUpdate[]>);
      
      // Show grouped notifications
      Object.entries(grouped).forEach(([type, notifications]) => {
        if (notifications.length === 1) {
          const notification = notifications[0];
          showNotification(notification);
        } else {
          // Show summary for multiple notifications
          showBatchNotification(type as ContentUpdate['type'], notifications);
        }
      });
      
    } finally {
      processingRef.current = false;
    }
  }, []);

  const showNotification = useCallback((notification: ContentUpdate) => {
    const messages = {
      new_content: {
        title: "New content discovered",
        description: notification.user_name 
          ? `${notification.user_name} shared: ${notification.title || 'New post'}`
          : notification.title || 'New content matches your interests'
      },
      trending: {
        title: "Trending now",
        description: notification.title || 'Something is gaining traction in your constellation'
      },
      constellation_update: {
        title: "Constellation evolved",
        description: notification.description || 'Your content constellation has been updated with new connections'
      }
    };

    const message = messages[notification.type];
    
    toast({
      title: message.title,
      description: message.description,
      duration: 4000,
    });
    
    log.info('Notification shown', { type: notification.type, id: notification.content_id });
  }, [toast]);

  const showBatchNotification = useCallback((type: ContentUpdate['type'], notifications: ContentUpdate[]) => {
    const count = notifications.length;
    
    const batchMessages = {
      new_content: {
        title: `${count} new posts discovered`,
        description: 'New content matches your current interests and mood'
      },
      trending: {
        title: `${count} items trending`,
        description: 'Multiple posts are gaining traction in your area'
      },
      constellation_update: {
        title: 'Constellation updated',
        description: `${count} new connections found in your content network`
      }
    };

    const message = batchMessages[type];
    
    toast({
      title: message.title,
      description: message.description,
      duration: 5000,
    });
    
    log.info('Batch notification shown', { type, count });
  }, [toast]);

  // Handle incoming real-time updates
  const handleContentUpdate = useCallback((payload: any) => {
    log.debug('Real-time update received', payload);
    
    // Transform database payload to notification
    const notification: ContentUpdate = {
      type: payload.eventType === 'INSERT' ? 'new_content' : 'constellation_update',
      content_id: payload.new?.id || payload.old?.id,
      title: payload.new?.title,
      description: payload.new?.body?.substring(0, 100),
      user_name: payload.new?.profiles?.display_name || payload.new?.profiles?.username,
      timestamp: new Date().toISOString()
    };
    
    // Apply preference filters
    if (notification.type === 'new_content' && !preferences.newContent) return;
    if (notification.type === 'constellation_update' && !preferences.constellationUpdates) return;
    
    // Add to queue for batch processing
    notificationQueueRef.current.push(notification);
    
    // Process queue after a short delay to allow batching
    setTimeout(processNotificationQueue, 500);
    
  }, [preferences, processNotificationQueue]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!preferences.newContent && !preferences.trending && !preferences.constellationUpdates) {
      return;
    }
    
    log.info('Subscribing to real-time notifications', preferences);
    
    // Subscribe to bulletins (posts) table for new content
    const channel = supabase
      .channel('contextboard-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bulletins',
          filter: 'is_active=eq.true'
        },
        handleContentUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bulletins',
          filter: 'is_relic=eq.true' // Notify when content becomes a relic
        },
        (payload) => {
          if (payload.new?.is_relic && !payload.old?.is_relic) {
            handleContentUpdate({
              ...payload,
              eventType: 'RELIC_PROMOTION'
            });
          }
        }
      )
      .subscribe((status) => {
        log.info('Real-time subscription status:', status);
        
        if (status === 'SUBSCRIBED' && !hasShownConnectedToast.current) {
          hasShownConnectedToast.current = true;
          toast({
            title: "Connected",
            description: "You'll receive live updates for new content",
            duration: 2000,
          });
        } else if (status === 'CHANNEL_ERROR') {
          log.error('Real-time subscription failed');
          toast({
            title: "Connection issue",
            description: "Real-time updates may be delayed",
            variant: "destructive",
            duration: 3000,
          });
        }
      });
    
    channelRef.current = channel;
    
    return () => {
      if (channelRef.current) {
        log.info('Unsubscribing from real-time notifications');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      // Reset the toast flag when component unmounts completely
      hasShownConnectedToast.current = false;
    };
  }, [preferences, handleContentUpdate, toast]);

  // Cleanup notification queue on unmount
  useEffect(() => {
    return () => {
      notificationQueueRef.current = [];
      processingRef.current = false;
    };
  }, []);

  const getQueueStatus = useCallback(() => ({
    queueLength: notificationQueueRef.current.length,
    isProcessing: processingRef.current,
    channelStatus: channelRef.current?.state
  }), []);

  return {
    getQueueStatus
  };
};